import { apiRequest } from "./axios";

/**
 * T&M Log API functions
 * Functions for managing T&M reports and pricing
 */

// Get all T&M logs
export async function getTMLogs() {
  const url = "/tmlog/reports/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get single T&M log by ID
export async function getTMLogById(id) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Get RAW attachment folder path for a T&M report (for listing/uploading attachments)
export async function getTMAttachmentFolderPath(reportId) {
  const url = `/tmlog/reports/${reportId}/attachment-folder-path/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Create new T&M report
export async function createTMReport(data) {
  const url = "/tmlog/reports/";
  return await apiRequest(url, data, { method: "POST" });
}

// Update existing T&M report
export async function updateTMReport(id, data) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

// Delete T&M report
export async function deleteTMReport(id) {
  const url = `/tmlog/reports/${id}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}

// Toggle submitted status for T&M report
export async function toggleTMReportSubmitted(id) {
  const url = `/tmlog/reports/${id}/toggle-submitted/`;
  return await apiRequest(url, {}, { method: "POST" });
}

// Get pricing by price ID
export async function getTMPrice(priceId) {
  const url = `/tmlog/prices/${priceId}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Get pricing by original report ID
export async function getTMPriceByReport(reportId) {
  const url = `/tmlog/prices/by-report/${reportId}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Create pricing for a report (creates snapshot from original report)
export async function createTMPrice(reportId, data) {
  const url = "/tmlog/prices/";
  return await apiRequest(url, { ...data, report: reportId }, { method: "POST" });
}

// Update pricing
export async function updateTMPrice(id, data) {
  const url = `/tmlog/prices/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

// Get reference data (contractors, PCO numbers, etc.)
export async function getTMReferenceData() {
  const url = "/tmlog/reference/reference_data/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get next available TM ID for a job
// Get next TM ID for a job (job number can be any string: "12345", "668-05", "0", etc.)
export async function getNextTMId(jobNumber) {
  const encoded = encodeURIComponent(String(jobNumber));
  const url = `/tmlog/generate-tm-id/${encoded}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// ===== Reference Data API Functions =====

// Get workers (active crew members) for Labor line items
// DEPRECATED: Use getUsersByRole({ role: 'crew', active: true }) from services/api/core instead
// Kept for backward compatibility but should use TMLogContext.workers
export async function getWorkers() {
  const { getUsersByRole } = await import("../api/core");
  return await getUsersByRole({ role: 'crew', active: true });
}

// Get materials (excluding Heavy Equipment and Unknown) for Material line items
export async function getMaterials() {
  const url = "/tmlog/reference/materials/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get equipment (Heavy Equipment only) for Equipment line items
export async function getEquipment() {
  const url = "/tmlog/reference/equipment/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get mix designs (concrete) for Concrete line items
export async function getMixDesigns() {
  const url = "/tmlog/reference/mix_designs/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get subcontractors for Subcontractor line items
export async function getSubcontractors() {
  const url = "/tmlog/reference/subcontractors/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get dumping options (uses SubContractor model) for Dumping line items
export async function getDumping() {
  const url = "/tmlog/reference/dumping/";
  return await apiRequest(url, null, { method: "GET" });
}

// Get units of measure for Material line items
export async function getUnits() {
  const url = "/tmlog/reference/units/";
  return await apiRequest(url, null, { method: "GET" });
}

// ===== Inspector API Functions =====

// Get inspector data by inspector token (PUBLIC - no authentication required)
export async function getInspectorByToken(token) {
  const url = `/tmlog/inspector/by-token/${token}/`;
  return await apiRequest(url, null, { method: "GET" });
}

// Submit inspector signature and comments (PUBLIC - no authentication required)
export async function submitInspectorSignature(data) {
  const url = "/tmlog/inspector/submit/";
  return await apiRequest(url, data, { method: "POST" });
}

// Send report to inspector (AUTHENTICATED)
export async function sendToInspector(reportId, data) {
  const url = `/tmlog/reports/${reportId}/send-to-inspector/`;
  return await apiRequest(url, data, { method: "POST" });
}

// Toggle locked status for T&M report (AUTHENTICATED)
export async function toggleTMReportLocked(id) {
  const url = `/tmlog/reports/${id}/toggle-locked/`;
  return await apiRequest(url, {}, { method: "POST" });
}
